import React, { useState } from 'react';
import { EditNote } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { Button } from '@mui/material';
import { updateBuilding } from 'shared/api/buildings';
import {
  DataFormatter,
  DataFormatterWithColumns,
} from 'shared/components/data-display/DataFormatter';
import { capitalize, dateString } from 'shared/utils';
import { complexEntity, managerEntity } from 'shared/entityDetails';
import { GridWrapper } from 'shared/components/data-display/GridFormatter';
import { DateEditor, TextAreaEditor, TextEditor } from 'shared/components/data-display/editors';
import { withAsyncSelect, withStaticSelect } from 'shared/components/data-display/withSelectors';
import { NewTabLink } from 'shared/components/abstract/interactables';
import NotesManager from 'shared/components/data-display/notesManager';
import HTML from 'shared/components/HTML';
import internetDescriptions from 'shared/components/data-display/internetDescriptions';
import { states } from './constants';

const addressDescriptions1 = [
  {
    key: 'name',
    label: 'Name',
    editor: TextEditor,
  },
  {
    key: 'address_1',
    label: 'Address Line 1',
    editor: TextEditor,
  },
  {
    key: 'address_2',
    label: 'Address Line 2',
    editor: TextEditor,
  },
];
const addressDescriptions2 = [
  {
    key: 'city',
    label: 'City',
    editor: TextEditor,
  },
  {
    key: 'zip',
    label: 'Zip',
    editor: TextEditor,
  },
  {
    key: 'state',
    label: 'State',
    editor: withStaticSelect({
      valueAsLabel: true,
      options: states,
      disableClearable: true,
    }),
  },
];

const ownerDescriptions = [
  {
    key: 'manager',
    formatter: (b) => b.manager.name,
    label: 'Manager',
    editor: withAsyncSelect(managerEntity),
    editKey: 'manager_id',
  },
  {
    key: 'complex',
    formatter: (b) => b.complex.name,
    label: 'Complex',
    editor: withAsyncSelect({
      ...complexEntity,
      label: 'Complex',
    }),
    editKey: 'complex_id',
  },
  {
    key: 'district_parcel',
    label: 'Parcel',
    editor: TextEditor,
  },
];
const contractDescriptions = [{
  key: 'contract_start',
  label: 'Contract Start',
  formatter: (building) => dateString(building.contract_start),
  editor: DateEditor,
},
{
  key: 'contract_end',
  label: 'Contract End',
  formatter: (building) => dateString(building.contract_end),
  editor: DateEditor,
}];

const networkingDescriptions1 = [
  {
    formatter: (dot1x) => dot1x?.nas_profile?.nas?.nas_name,
    label: 'Main Router',
    readOnly: true,
  },
  {
    formatter: (dot1x) => dot1x?.nas_profile?.ip,
    label: 'VTR',
    readOnly: true,
  },
];

const networkingDescriptions2 = [
  {
    key: 'vlan_distribution_type',
    label: 'VLAN Distribution',
    formatter: (building) => capitalize(building.dot1x?.vlan_distribution_type),
    readOnly: true,
  },
  {
    key: 'vlan_range_start',
    label: 'VLAN Start',
    readOnly: true,
  },
  {
    key: 'vlan_range_end',
    label: 'VLAN End',
    readOnly: true,
  },
];

const extraDescriptions = [
  {
    key: 'sa_code',
    label: 'SA Code',
    editor: TextEditor,
  },
  {
    key: 'wug_link',
    label: 'WUG',
    editor: TextEditor,
    formatter: (b) => (b.wug_link.startsWith('http')
      ? <NewTabLink link={b.wug_link} text={`${b.name} WUG Link`} /> : b.wug_link),
  },
  {
    key: 'updated_at',
    label: 'Last Updated',
    readOnly: true,
    formatter: (building) => dateString(building.updated_at),
  },
  {
    key: 'updated_by',
    label: 'Updated By',
    readOnly: true,
  },
];

const notesDescription = [
  {
    key: 'notes',
    label: 'Legacy Notes',
    editor: TextAreaEditor,
    formatter: (b) => <HTML content={b.notes} />,
  },
];

/**
 * This is used in both the building creator and building view.
 * @param {Object<string, *>} building - The building object
 * @param {boolean} editEnabled - Whether editing is enabled
 * @param {Object<string, *>} updatedData - The updated data
 * @param {Function} updateValue - Function to update a value
 * @param {[buildingDispatch]} buildingDispatch - Optional dispatch function for building state
 */
function BuildingInfo({
  building,
  editEnabled,
  updatedData,
  updateValue,
  buildingDispatch,
}) {
  const [editingNotes, setEditingNotes] = useState(false);

  const {
    id,
    dot1x,
  } = building;

  return (
    <Grid container spacing={2}>
      <GridWrapper
        size={{
          lg: 6,
          xs: 12,
        }}
        title="Address"
      >
        <DataFormatter
          descriptions={addressDescriptions1}
          editing={editEnabled}
          data={building}
          updatedData={updatedData}
          updateValue={updateValue}
        />
        <DataFormatterWithColumns
          descriptions={addressDescriptions2}
          editing={editEnabled}
          data={building}
          numberOfColumns={3}
          updatedData={updatedData}
          updateValue={updateValue}
        />
      </GridWrapper>
      <GridWrapper
        size={{
          lg: 6,
          xs: 12,
        }}
        title="Ownership"
      >
        <DataFormatter
          descriptions={ownerDescriptions}
          editing={editEnabled}
          data={building}
          updatedData={updatedData}
          updateValue={updateValue}
        />
        <DataFormatterWithColumns
          descriptions={contractDescriptions}
          editing={editEnabled}
          data={building}
          numberOfColumns={2}
          updatedData={updatedData}
          updateValue={updateValue}
        />
      </GridWrapper>
      <GridWrapper
        size={{
          lg: 6,
          xs: 12,
        }}
        title="Internet"
      >
        <DataFormatterWithColumns
          descriptions={internetDescriptions}
          editing={editEnabled}
          data={building}
          numberOfColumns={2}
          updatedData={updatedData}
          updateValue={updateValue}
        />
        {id && (
          <>
            <DataFormatterWithColumns
              descriptions={networkingDescriptions1}
              data={dot1x || {}}
              numberOfColumns={2}
              updatedData={updatedData}
              updateValue={updateValue}
            />
            <DataFormatterWithColumns
              descriptions={networkingDescriptions2}
              data={building}
              numberOfColumns={3}
              updatedData={updatedData}
              updateValue={updateValue}
            />
          </>
        )}
      </GridWrapper>
      <GridWrapper
        size={{
          lg: 6,
          xs: 12,
        }}
        title="Misc."
      >
        <DataFormatterWithColumns
          descriptions={extraDescriptions}
          editing={editEnabled}
          data={building}
          numberOfColumns={2}
          updatedData={updatedData}
          updateValue={updateValue}
        />
      </GridWrapper>
      {id && (
        <>
          {/* Building Notes */}
          <GridWrapper
            size={{
              lg: 4,
              xs: 12,
            }}
            title="Building Notes"
            titleIcon={(
              <Button
                variant="outlined"
                startIcon={<EditNote />}
                onClick={() => {
                  if (!building.id) return;
                  setEditingNotes(true);
                }}
                size="small"
              >
                Edit
              </Button>
            )}
          >
            <NotesManager
              title="Edit building notes"
              entityType="Building"
              entityID={building.id}
              isEditing={editingNotes}
              onStopEditing={() => setEditingNotes(false)}
              onStartEditing={() => setEditingNotes(true)}
              legacyNotes={building.notes}
              entityConfig={{
                body: building,
                update: updateBuilding,
                dispatch: buildingDispatch,
              }}
            />
            {building.notes && (
              <DataFormatter
                descriptions={notesDescription}
                editing={false}
                data={building}
                updatedData={updatedData}
                updateValue={updateValue}
                copyable
                multiline
              />
            )}
          </GridWrapper>

          {/* Complex read-only notes  */}
          {!!building?.complex?.id && (
            <GridWrapper
              size={{
                lg: 4,
                xs: 12,
              }}
              title="Complex Notes"
            >
              <NotesManager
                title="Edit complex notes"
                entityType="Complex"
                entityID={building.complex.id}
                readonly
                helperText="No Available Notes"
              />
            </GridWrapper>
          )}

          {/* Property manager read-only notes */}
          {!!building?.manager?.id && (
            <GridWrapper
              size={{
                lg: 4,
                xs: 12,
              }}
              title="Manager Notes"
            >
              <NotesManager
                title="Edit manager notes"
                entityType="Property Manager"
                entityID={building.manager.id}
                readonly
                helperText="No Available Notes"
              />
            </GridWrapper>
          )}
        </>
      )}
    </Grid>
  );
}

export default BuildingInfo;
